import {
  Chart,
  ChartDataset,
  ChartOptions,
  ChartType,
  FontSpec,
  TooltipModel,
  TooltipXAlignment,
  TooltipYAlignment,
} from 'chart.js';

export const BAR_BORDER_RADIUS = 5;
const BAR_THICKNESS = 10;
export const GREEN_BAR_COLOR = '#00FF47';
export const CYAN_BAR_COLOR = '#00FFFF';
export const GRID_COLOR = '#CECECE';
export const TICK_COLOR = '#000000';
const Y_AXES_TICKS_COUNT = 6;
const Y_AXES_STEP_SIZE_PX = 20;
const X_AXES_FONT_FAMILY = 'TT Interphases Trl';
const X_AXES_FONT_SIZE = 10;
export const X_AXES_FONT: Partial<FontSpec> = {
  family: X_AXES_FONT_FAMILY,
  size: X_AXES_FONT_SIZE,
  weight: 400,
};
const Y_AXES_FONT: Partial<FontSpec> = {
  family: X_AXES_FONT_FAMILY,
  size: X_AXES_FONT_SIZE,
  weight: 700,
};
export const X_SCALE_OPTIONS = {
  grid: {
    display: false,
  },
  ticks: {
    font: X_AXES_FONT,
    color: TICK_COLOR,
  },
};
export const Y_TICKS_COMMON_OPTIONS = {
  count: Y_AXES_TICKS_COUNT,
  font: Y_AXES_FONT,
  color: TICK_COLOR,
};
export const Y_SCALE_COMMON_OPTIONS = {
  beginAtZero: true,
  grid: {
    color: GRID_COLOR,
  },
  offset: false,
  ticks: {
    ...Y_TICKS_COMMON_OPTIONS,
    stepSize: Y_AXES_STEP_SIZE_PX,
  },
};

export type TooltipContext<T extends ChartType> = {
  chart: Chart;
  tooltip: TooltipModel<T>;
  replay?: boolean;
};

export function createBasicChartOptions(
  updateTooltip: (context: TooltipContext<'bar'>) => void,
): ChartOptions {
  return {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      intersect: false,
      mode: 'index',
    },
    plugins: {
      tooltip: {
        enabled: false,
        external: (context: TooltipContext<'bar'>) => {
          if (context.replay) {
            return; // Infinite change detection loop prevention
          }
          updateTooltip(context);
        },
      },
    },
    animation: false,
  };
}

type BarDatasetOptions<T> = {
  xAxisKey: string;
  yAxisKey: string;
  barColor: string;
  chartData: T;
};

export function createBarDataset<T>({
  xAxisKey,
  yAxisKey,
  barColor,
  chartData,
}: BarDatasetOptions<T>): ChartDataset<'bar', T> {
  return {
    type: 'bar',
    barThickness: BAR_THICKNESS,
    backgroundColor: barColor,
    hoverBackgroundColor: barColor,
    data: chartData,
    parsing: {
      xAxisKey,
      yAxisKey,
    },
  };
}

export type TooltipDisplayParameters = {
  left: number;
  top: number;
  visible: boolean;
  xAlign: TooltipXAlignment;
  yAlign: TooltipYAlignment;
};

export const EMPTY_TOOLTIP_DISPLAY_PARAMETERS: TooltipDisplayParameters = {
  left: 0,
  top: 0,
  xAlign: 'center',
  yAlign: 'center',
  visible: false,
};

export function calculateDisplayParameters(
  context: TooltipContext<'bar'>,
): TooltipDisplayParameters {
  const position = context.chart.canvas.getBoundingClientRect();
  return {
    left: position.left + window.pageXOffset + context.tooltip.caretX,
    top: position.top + window.pageYOffset + context.tooltip.caretY,
    visible: context.tooltip.opacity !== 0,
    xAlign: context.tooltip.xAlign,
    yAlign: context.tooltip.yAlign,
  };
}
